import * as React from 'react';
import { FormikHelpers as FormikActions, Formik, FormikProps } from 'formik';
import { TwsAdmin } from '@weddingspot/ws-api-client';
import { SpinnerOverlay } from '@weddingspot/ws-core-components';

import { AmenitiesFormData } from './types';
import { defaultAmenitiesFromData } from './constants';
import { anyTouched } from '../../utils/formik';
import AmenitiesCard, { AmenityType } from './AmenitiesCard';
import PoliciesCard from './PoliciesCard';

interface OwnProps {
    /* Form data */
    currentVenue: TwsAdmin.AvailableVenue;
    choicesLoaded: boolean | null;
    choices: TwsAdmin.AmenitiesFormChoices | null;
    valuesLoaded: boolean | null;
    initialValues: AmenitiesFormData | null;
    extraAmenities: TwsAdmin.ExtraAmenity[];
    extraRestrictions: TwsAdmin.ExtraAmenity[];

    /* Form handlers */
    onSubmit: (formData: AmenitiesFormData, formikActions: FormikActions<AmenitiesFormData>) => Promise<void>;

    // Notify parent that an initialization error occurred
    onInitError: () => void;

    // Notify parent that a form error occurred
    onFormError: () => void;

    // Notify parent that form was successfully submitted
    onFormSuccess: () => void;

    // Notify parent that form was touched
    setTouched: (touched: boolean) => void;

    // Handle the deletion of an extra amenity
    handleCreateExtraAmenity: (name: string, amenityType: AmenityType) => Promise<void>;
    handleDeleteExtraAmenity: (amenity: TwsAdmin.ExtraAmenity) => Promise<void>;
}

export class Amenities extends React.Component<OwnProps> {
    constructor(props: OwnProps) {
        super(props);
    }

    render() {
        const {
            choices,
            choicesLoaded,
            valuesLoaded,
            initialValues,
            extraAmenities,
            extraRestrictions,
            handleDeleteExtraAmenity,
            handleCreateExtraAmenity,
        } = this.props;

        return (
            <div className='VenueManagement--amenities'>
                <Formik
                    initialValues={initialValues || defaultAmenitiesFromData}
                    enableReinitialize={true}
                    validate={() => {
                        const errors: Partial<AmenitiesFormData> = {};
                        return errors;
                    }}
                    onSubmit={this.props.onSubmit}
                >
                    {(formikProps: FormikProps<AmenitiesFormData>) => {
                        const { values, handleChange, handleSubmit, setFieldValue, touched, isSubmitting } = formikProps;

                        // remove and tmp field names
                        let cleanedTouched = {};
                        Object.keys(touched)
                            .filter((k) => !k.startsWith('__'))
                            .map((k) => (cleanedTouched[k] = touched[k]));

                        this.props.setTouched(anyTouched(cleanedTouched));

                        if (typeof window !== 'undefined') {
                            window.onbeforeunload = () => {
                                if (anyTouched(touched)) {
                                    // NOTE: Chromium browsers diabled custom messages
                                    // including this here in case the target browser
                                    // still supports this :(
                                    return 'You have unsaved changes! Are you sure' + ' you want to leave before saving them?';
                                }

                                return;
                            };
                        }

                        const buttonSpinnerProps = {
                            isSpinning: isSubmitting,
                            spinnerProps: {
                                spinnerColor: '#881155',
                                spinnerDiameter: 40,
                            },
                        };

                        const cardSpinnerProps = {
                            isSpinning: isSubmitting || choicesLoaded === null || valuesLoaded === null,
                            spinnerProps: {
                                spinnerColor: '#881155',
                                spinnerDiameter: 60,
                            },
                        };

                        return (
                            <div>
                                <form onSubmit={handleSubmit} className='VenueManagement--amenities-form'>
                                    <div className='VenueManagement--body-header'>
                                        <div className='VenueManagement--body-header-title'>Amenities</div>
                                        {!!formikProps.status && (
                                            <div className='VenueManagement--field-level-error'>{formikProps.status}</div>
                                        )}
                                        <div className='VenueManagement--body-header--submit-container'>
                                            <SpinnerOverlay {...buttonSpinnerProps}>
                                                <button
                                                    type='submit'
                                                    className='VenueManagement--form-submit nx-button nx-button--primary'
                                                    disabled={!choicesLoaded || !valuesLoaded}
                                                >
                                                    Save
                                                </button>
                                            </SpinnerOverlay>
                                        </div>
                                    </div>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <AmenitiesCard
                                            amenityType={AmenityType.AMENITY}
                                            choicesLoaded={choicesLoaded}
                                            choicesData={choices}
                                            extraAmenities={extraAmenities}
                                            valuesLoaded={valuesLoaded}
                                            formikProps={formikProps}
                                            handleDeleteExtraAmenity={handleDeleteExtraAmenity}
                                            handleCreateExtraAmenity={(name) => handleCreateExtraAmenity(name, AmenityType.AMENITY)}
                                        />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <AmenitiesCard
                                            amenityType={AmenityType.RESTRICTION}
                                            choicesLoaded={choicesLoaded}
                                            choicesData={choices}
                                            valuesLoaded={valuesLoaded}
                                            formikProps={formikProps}
                                            extraAmenities={extraRestrictions}
                                            handleDeleteExtraAmenity={handleDeleteExtraAmenity}
                                            handleCreateExtraAmenity={(name) => handleCreateExtraAmenity(name, AmenityType.RESTRICTION)}
                                        />
                                    </SpinnerOverlay>
                                    <SpinnerOverlay {...cardSpinnerProps}>
                                        <PoliciesCard
                                            choicesLoaded={choicesLoaded}
                                            choicesData={choices}
                                            valuesLoaded={valuesLoaded}
                                            formikProps={formikProps}
                                        />
                                    </SpinnerOverlay>
                                    <div className='VenueManagement--body-footer'>
                                        <div className='VenueManagement--body-footer--submit-container'>
                                            <SpinnerOverlay {...buttonSpinnerProps}>
                                                <button
                                                    type='submit'
                                                    className='VenueManagement--form-submit nx-button nx-button--primary'
                                                    disabled={!choicesLoaded || !valuesLoaded}
                                                >
                                                    Save
                                                </button>
                                            </SpinnerOverlay>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        );
                    }}
                </Formik>
            </div>
        );
    }
}
